import React from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import clsx from "clsx";
import {
  Typography,
  Drawer,
  IconButton,
  List,
  ListItem,
  Divider,
  makeStyles,
  Menu,
  MenuItem,
} from "@material-ui/core";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import AssignmentIcon from "@material-ui/icons/Assignment";
import PermMediaIcon from "@material-ui/icons/PermMedia";
import PublicIcon from "@material-ui/icons/Public";
import LayersIcon from "@material-ui/icons/Layers";
import PeopleIcon from "@material-ui/icons/People";
import DescriptionIcon from "@material-ui/icons/Description";
import LabelIcon from "@material-ui/icons/Label";
import AccountCircleIcon from "@material-ui/icons/AccountCircle";
import MoreHorizIcon from "@material-ui/icons/MoreHoriz";
import WebIcon from "@material-ui/icons/Web";
import MapIcon from '@mui/icons-material/Map';

const useSideBarItemStyles = makeStyles((theme) => ({
  listItem: {
    padding: 0,
    margin: 0,
  },
  mainItem: {
    flex: "1",
    padding: ".75rem",
    minWidth: "45px",
    display: "flex",
    alignItems: "stretch",
    color: theme.palette.primary.main,
    textDecoration: "none",
  },
  mainItemLabel: {
    marginLeft: "1rem",
  },
  nested: {
    paddingLeft: theme.spacing(4),
  },
}));

function SideBarItem({ label, icon, link, subMenuItems }) {
  const classes = useSideBarItemStyles();
  const [anchorEl, setAnchorEl] = React.useState(null);

  const openMenu = (e) => {
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <ListItem className={classes.listItem} button>
        <Typography
          component={Link}
          to={`/${process.env.REACT_APP_ADMIN_URL}${link}`}
          className={classes.mainItem}
        >
          {icon}
          <div className={classes.mainItemLabel}>{label}</div>
        </Typography>
        {subMenuItems && (
          <>
            <IconButton
              style={{ padding: ".5rem", marginRight: ".5rem" }}
              onClick={openMenu}
              size="small"
            >
              <MoreHorizIcon color="secondary" />
            </IconButton>
            <Menu
              id="sub-menu"
              anchorEl={anchorEl}
              keepMounted
              open={Boolean(anchorEl)}
              onClose={handleClose}
            >
              {subMenuItems.map((item, index) => (
                <MenuItem
                  component={Link}
                  to={`/${process.env.REACT_APP_ADMIN_URL}${item.link}`}
                  key={index}
                  onClick={handleClose}
                >
                  {item.title}
                </MenuItem>
              ))}
            </Menu>
          </>
        )}
      </ListItem>
    </>
  );
}

function SideBar({
  classes,
  theme,
  handleDrawerClose,
  handleDrawerOpen,
  open,
}) {
  const { user } = useAuth();
  return (
    <Drawer
      variant="permanent"
      className={clsx(classes.drawer, {
        [classes.drawerOpen]: open,
        [classes.drawerClose]: !open,
      })}
      classes={{
        paper: clsx({
          [classes.drawerOpen]: open,
          [classes.drawerClose]: !open,
        }),
      }}
    >
      <div className={classes.toolbar}>
        <IconButton onClick={handleDrawerClose}>
          {theme.direction === "rtl" ? (
            <ChevronRightIcon />
          ) : (
            <ChevronLeftIcon />
          )}
        </IconButton>
      </div>
      <Divider />
      <List>
        <SideBarItem label="Documentation" icon={<AssignmentIcon />} link="/" />
        <SideBarItem
          label="Files"
          icon={<PermMediaIcon />}
          link="/files"
          subMenuItems={[
            { title: "View Files", link: "/files" },
            { title: "Add Files", link: "/files/add" },
          ]}
        />
        <SideBarItem
          label="Sites"
          icon={<PublicIcon />}
          link="/sites"
          subMenuItems={[
            { title: "View Sites", link: "/sites" },
            { title: "Add Site", link: "/sites/add" },
            { title: "Menus", link: "/menus" },
            { title: "External Links", link: "/externalsites" },
          ]}
        />
        <SideBarItem
          label="Pages"
          icon={<WebIcon />}
          link="/pages"
          subMenuItems={[
            { title: "View Pages", link: "/pages" },
            { title: "Add Page", link: "/pages/add" },
          ]}
        />
        <SideBarItem
          label="Units"
          icon={<LayersIcon />}
          link="/units"
          subMenuItems={[
            { title: "View Units", link: "/units" },
            { title: "Add Unit", link: "/units/add" },
          ]}
        />
        <SideBarItem
          label="People"
          icon={<PeopleIcon />}
          link="/people"
          subMenuItems={[
            { title: "View People", link: "/people" },
            { title: "Add Person", link: "/people/add" },
          ]}
        />
        <SideBarItem
          label="Entries"
          icon={<DescriptionIcon />}
          link="/entries"
          subMenuItems={[
            { title: "View Entries", link: "/entries" },
            { title: "Add Entry", link: "/entries/add" },
          ]}
        />
        <SideBarItem
          label="Themes"
          icon={<LabelIcon />}
          link="/themes"
          subMenuItems={[
            { title: "View Themes", link: "/themes" },
            { title: "Add Theme", link: "/themes/add" },
          ]}
        />
        <SideBarItem
          label="Regions"
          icon={<MapIcon />}
          link="/regions"
          subMenuItems={[
            { title: "View Regions", link: "/regions" },
            { title: "Add Region", link: "/regions/add" },
          ]}
        />
        {user.role === "admin" && (
          <>
            <Divider />
            <SideBarItem
              label="Users"
              icon={<AccountCircleIcon />}
              link="/users"
              subMenuItems={[
                { title: "View Users", link: "/users" },
                { title: "Add User", link: "/users/add" },
              ]}
            />
          </>
        )}
      </List>
    </Drawer>
  );
}

export default SideBar;
